import { LOAD_STATE, STOP_TYPE } from 'apps/fleet-planner/constants/loads'
import { IStop } from 'apps/fleet-planner/types/loads'
import { getStopNumbers } from 'apps/fleet-planner/utils/stopNumbers'
import { capitalize } from 'utils/capitalize'
import { formatCityState } from 'utils/formatCityState'

interface IStopData {
  isLastPickup: boolean
  isLastDropoff: boolean
  isMultiPickup: boolean
  isMultiDropoff: boolean
  currentStopType: STOP_TYPE
}

export interface IGetNextDriverStatus {
  nextStatus?: LOAD_STATE
  nextSequence: number | null
  CtaLabel: string
  CtaHeader: string
}

export const CHECK_IN_STATES = [LOAD_STATE['At Pickup'], LOAD_STATE['At Dropoff']]

export const CHECK_OUT_STATES = [LOAD_STATE['In Transit']]

export const CTA_STATUS_UPDATE_STATES = [
  LOAD_STATE.Dispatched,
  LOAD_STATE['At Pickup'],
  LOAD_STATE['In Transit'],
  LOAD_STATE['At Dropoff'],
  LOAD_STATE.Loading,
  LOAD_STATE.Unloading,
]

export const CTA_UPLOAD_POD_STATES = [LOAD_STATE.Delivered]

export const stopsLength = (stops: IStop[] = [], stopType: STOP_TYPE): number =>
  stops.filter(s => s.stopType === stopType).length

export const isMultiStop = (stop?: IStop, stops: IStop[] = []): boolean => {
  if (!stop) return false
  const lengthofStops = stopsLength(stops, stop.stopType)
  return lengthofStops > 1
}

export const getCurrentStopData = (stops: IStop[] = [], currentStopSequence: number): IStopData => {
  const pickupLength = stopsLength(stops, STOP_TYPE.pickup)
  const dropoffLength = stopsLength(stops, STOP_TYPE.dropoff)
  const isLastPickup = currentStopSequence === pickupLength - 1
  const isLastDropoff = currentStopSequence === dropoffLength + pickupLength
  const isMultiPickup = pickupLength > 1
  const isMultiDropoff = dropoffLength > 1
  const currentStopType =
    stops.find(stop => stop.stopSequence === currentStopSequence)?.stopType || STOP_TYPE.pickup
  return { isLastPickup, isLastDropoff, isMultiPickup, isMultiDropoff, currentStopType }
}

export const getNextCityState = (stopSequence: number, stops: IStop[] = []): string => {
  const nextStop = stops.find(stop => stop.stopSequence === stopSequence) || stops[stopSequence - 1]
  const { address } = nextStop || {}
  const { locality = '', subdivisionCode = '' } = address || {}
  return formatCityState(locality, subdivisionCode) || ''
}

export const getStopTypeBySequenceNumber = (stopSequence: number, stops: IStop[] = []) => {
  const nextStop = stops.find(stop => stop.stopSequence === stopSequence) || stops[stopSequence - 1]
  const { stopType } = nextStop || {}
  return stopType || ''
}

export const getStopNumber = (ifMultiStop: boolean, stopSequence: number, stops: IStop[]) => {
  const stopNumbers = getStopNumbers(stops)
  return ifMultiStop ? ` ${stopNumbers[stopSequence - 1]}` : ''
}

export const getNextDriverStatus = (
  loadStatusKey: LOAD_STATE,
  currentStopSequence: number,
  stops: IStop[]
): IGetNextDriverStatus => {
  const {
    currentStopType,
    isLastDropoff,
    isLastPickup,
    isMultiDropoff,
    isMultiPickup,
  } = getCurrentStopData(stops, currentStopSequence)
  const nextStopSequence = currentStopSequence + 1
  if (loadStatusKey === LOAD_STATE['Ready for Dispatch']) {
    return {
      nextStatus: LOAD_STATE.Dispatched,
      nextSequence: null,
      CtaLabel: 'Dispatched',
      CtaHeader: 'Have you been dispatched?',
    }
  }
  if (loadStatusKey === LOAD_STATE['At Pickup'] || loadStatusKey === LOAD_STATE.Loading) {
    const stopType = getStopTypeBySequenceNumber(nextStopSequence, stops) || ''
    return {
      nextStatus: LOAD_STATE['In Transit'],
      nextSequence: nextStopSequence,
      CtaLabel: `In Transit to ${capitalize(stopType)}${getStopNumber(
        isMultiPickup,
        nextStopSequence,
        stops
      )}`,
      CtaHeader: isMultiPickup
        ? `Are you in transit to ${getNextCityState(nextStopSequence, stops)}?`
        : `Are you in transit to your ${getStopTypeBySequenceNumber(
            nextStopSequence,
            stops
          )} in ${getNextCityState(nextStopSequence, stops)}?`,
    }
  }
  if ([loadStatusKey].includes(LOAD_STATE['In Transit']) && currentStopType === STOP_TYPE.pickup) {
    const stopType = currentStopType || ''
    const isMulti = isLastPickup ? isMultiDropoff : isMultiPickup
    return {
      nextStatus: isLastPickup ? LOAD_STATE['At Dropoff'] : LOAD_STATE['At Pickup'],
      nextSequence: currentStopSequence,
      CtaLabel: `Arrived At ${capitalize(stopType)}${getStopNumber(
        isMulti,
        currentStopSequence,
        stops
      )}`,
      CtaHeader: isMulti
        ? `Are you at ${capitalize(stopType)}${getStopNumber(
            isMulti,
            currentStopSequence,
            stops
          )} in ${getNextCityState(currentStopSequence, stops)}?`
        : `Are you at the ${stopType} in ${getNextCityState(currentStopSequence, stops)}?`,
    }
  }
  if ([loadStatusKey].includes(LOAD_STATE['In Transit']) && currentStopType === STOP_TYPE.dropoff) {
    const stopType = currentStopType || ''
    return {
      nextStatus: LOAD_STATE['At Dropoff'],
      nextSequence: currentStopSequence,
      CtaLabel: `Arrived At ${capitalize(stopType)}${getStopNumber(
        isMultiDropoff,
        currentStopSequence,
        stops
      )}`,
      CtaHeader: isMultiDropoff
        ? `Are you at ${capitalize(stopType)}${getStopNumber(
            isMultiDropoff,
            currentStopSequence,
            stops
          )} in ${getNextCityState(currentStopSequence, stops)}?`
        : `Are you at your ${currentStopType} in ${getNextCityState(currentStopSequence, stops)}?`,
    }
  }

  if (loadStatusKey === LOAD_STATE['At Dropoff'] || loadStatusKey === LOAD_STATE.Unloading) {
    const stopType = currentStopType || ''
    if (isLastDropoff) {
      return {
        nextStatus: LOAD_STATE.Delivered,

        nextSequence: null,
        CtaLabel: 'Delivered',
        CtaHeader: 'Did you complete your delivery?',
      }
    }
    return {
      nextStatus: LOAD_STATE['In Transit'],
      nextSequence: nextStopSequence,
      CtaLabel: isMultiDropoff
        ? `In Transit to ${capitalize(stopType)}${getStopNumber(
            isMultiDropoff,
            nextStopSequence,
            stops
          )}`
        : `Arrived At Dropoff${getStopNumber(isMultiDropoff, nextStopSequence, stops)}`,
      CtaHeader: isMultiDropoff
        ? `Are you in transit to ${getNextCityState(nextStopSequence, stops)}?`
        : `Are you at your dropoff in ${getNextCityState(nextStopSequence, stops)}`,
    }
  }

  if (loadStatusKey === LOAD_STATE.Delivered) {
    return {
      nextStatus: undefined,

      nextSequence: null,
      CtaLabel: 'Upload POD',
      CtaHeader: '',
    }
  }

  return {
    nextStatus: LOAD_STATE['At Pickup'],
    nextSequence: 1,
    CtaLabel: `Arrived At Pickup${getStopNumber(isMultiPickup, 1, stops)}`,
    CtaHeader: isMultiPickup
      ? `Are you at Pickup${getStopNumber(isMultiPickup, 1, stops)} in ${getNextCityState(
          1,
          stops
        )}?`
      : `Are you at the pickup in ${getNextCityState(1, stops)}?`,
  }
}
